'use client';

import { cn } from '@/shared/utils/formatting';
import { PASSWORD_REQUIREMENTS, getPasswordStrength } from '@/shared/utils/password-validation';
import { Check, Eye, EyeOff, X } from 'lucide-react';
import { forwardRef, useState, type InputHTMLAttributes } from 'react';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  showPasswordToggle?: boolean;
  showPasswordStrength?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  floatingLabel?: boolean;
}

// Password requirements component using shared validation
function PasswordRequirementsInline({ password }: { password: string }) {
  const requirements = PASSWORD_REQUIREMENTS.map((req) => ({
    label: req.label,
    met: req.test(password),
  }));

  return (
    <div className="mt-2 space-y-1">
      {requirements.map((req) => (
        <div key={req.label} className="flex items-center gap-2 text-xs">
          {req.met ? (
            <Check className="w-3 h-3 text-green-500" />
          ) : (
            <X className="w-3 h-3 text-text-secondary" />
          )}
          <span className={req.met ? 'text-green-500' : 'text-text-secondary'}>{req.label}</span>
        </div>
      ))}
    </div>
  );
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      label,
      error,
      helperText,
      type = 'text',
      id,
      showPasswordToggle,
      showPasswordStrength,
      leftIcon,
      rightIcon,
      floatingLabel,
      value,
      ...props
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = useState(false);
    const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');
    const isPassword = type === 'password';
    const actualType = isPassword && showPassword ? 'text' : type;
    const passwordValue = (value as string) || '';
    const strength = showPasswordStrength && isPassword ? getPasswordStrength(passwordValue) : null;

    return (
      <div className="w-full">
        {label && !floatingLabel && (
          <label htmlFor={inputId} className="block text-sm font-medium text-text-primary mb-1.5">
            {label}
          </label>
        )}
        <div className="relative">
          {label && floatingLabel && (
            <span
              className={cn(
                'absolute top-2 text-2xs font-semibold uppercase tracking-wider text-white/50 pointer-events-none z-10',
                leftIcon ? 'left-10' : 'left-4'
              )}
            >
              {label.replace(/\s*\*$/, '')}
              {(label.endsWith('*') || props.required) && (
                <span className="ml-0.5 text-red-primary">*</span>
              )}
            </span>
          )}
          {leftIcon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary">
              {leftIcon}
            </div>
          )}
          <input
            ref={ref}
            id={inputId}
            type={actualType}
            value={value}
            className={cn(
              'w-full px-4 py-2.5 rounded border text-base transition-all duration-200',
              'bg-surface-glass',
              'text-text-primary',
              'placeholder-text-secondary/60',
              'focus:outline-none focus:ring-2 focus:ring-offset-0 focus:ring-offset-background',
              error
                ? 'border-red-primary focus:border-red-primary focus:ring-red-primary/30'
                : 'border-border hover:border-border-hover focus:border-red-primary focus:ring-red-primary/30',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              floatingLabel && 'pt-6 pb-2',
              leftIcon && 'pl-10',
              (rightIcon || (isPassword && showPasswordToggle)) && 'pr-10',
              className
            )}
            {...props}
          />
          {isPassword && showPasswordToggle !== false && (
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary transition-colors p-1"
              tabIndex={-1}
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          )}
          {rightIcon && !isPassword && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary">
              {rightIcon}
            </div>
          )}
        </div>

        {/* Password strength indicator */}
        {strength && passwordValue.length > 0 && (
          <div className="mt-2">
            <div className="flex items-center gap-2">
              <div className="flex-1 h-1.5 bg-surface rounded-full overflow-hidden">
                <div
                  className={cn('h-full transition-all duration-300', strength.color)}
                  style={{ width: `${(strength.score / 5) * 100}%` }}
                />
              </div>
              <span className={cn('text-xs font-medium', strength.color.replace('bg-', 'text-'))}>
                {strength.label}
              </span>
            </div>
            <PasswordRequirementsInline password={passwordValue} />
          </div>
        )}

        {error && <p className="mt-1.5 text-sm text-red-primary">{error}</p>}
        {helperText && !error && !strength && (
          <p className="mt-1.5 text-sm text-text-secondary">{helperText}</p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export { Input };
